import { mediaQueryLarge, isMobileBreakpoint } from '@theme/utilities';

// Accordion
// Still extends HTMLElement over Component so that refs are still available to parent components (e.g. SortingFilterComponent)
class AccordionCustom extends HTMLElement {
  /** @type {HTMLDetailsElement} */
  get details() {
    const details = this.querySelector('details');

    if (!(details instanceof HTMLDetailsElement)) throw new Error('Details element not found');

    return details;
  }

  /** @type {HTMLElement} */
  get summary() {
    const summary = this.details.querySelector('summary');

    if (!(summary instanceof HTMLElement)) throw new Error('Summary element not found');

    return summary;
  }

  get #disableOnMobile() {
    return this.dataset.disableOnMobile === 'true';
  }

  get #disableOnDesktop() {
    return this.dataset.disableOnDesktop === 'true';
  }

  get #closeWithEscape() {
    return this.dataset.closeWithEscape === 'true';
  }

  #controller = new AbortController();

  connectedCallback() {
    const { signal } = this.#controller;

    this.#setDefaultOpenState();
    this.#applyAccordionColorScheme(signal);
    this.#applyExclusiveOpen(signal);

    this.addEventListener('keydown', this.#handleKeyDown, { signal });
    this.summary.addEventListener('click', this.handleClick, { signal });
    mediaQueryLarge.addEventListener('change', this.#handleMediaQueryChange, { signal });
  }

  /**
   * Handles the disconnect event.
   */
  disconnectedCallback() {
    // Disconnect all the event listeners
    this.#controller.abort();
  }

  /**
   * Handles the click event.
   * @param {Event} event - The event.
   */
  handleClick = (event) => {
    const isMobile = isMobileBreakpoint();
    const isDesktop = !isMobile;

    // Stop default behaviour from the browser
    if ((isMobile && this.#disableOnMobile) || (isDesktop && this.#disableOnDesktop)) {
      event.preventDefault();
      return;
    }
  };

  /**
   * Handles the media query change event.
   */
  #handleMediaQueryChange = () => {
    this.#setDefaultOpenState();
  };

  /**
   * Sets the default open state of the accordion based on the `open-by-default-on-mobile` and `open-by-default-on-desktop` attributes.
   */
  #setDefaultOpenState() {
    const isMobile = isMobileBreakpoint();

    this.details.open =
      (isMobile && this.hasAttribute('open-by-default-on-mobile')) ||
      (!isMobile && this.hasAttribute('open-by-default-on-desktop'));
  }

  /**
   * Handles the color scheme classes when the accordion is opened or closed.
   *
   * @param {AbortSignal} signal
   */
  #applyAccordionColorScheme(signal) {
    const accordion = this.closest('.accordion');

    if (!(accordion instanceof HTMLElement)) return;

    const { openScheme, closedScheme } = accordion.dataset;

    if (!openScheme || !closedScheme) return;

    const openClass = `color-${openScheme}`;
    const closedClass = `color-${closedScheme}`;
    const details = this.details;

    details.classList.remove(openClass, closedClass);
    details.classList.add(details.open ? openClass : closedClass);

    this.summary?.classList.toggle(closedClass, details.open);

    details.addEventListener(
      'toggle',
      () => {
        details.classList.remove(openClass, closedClass);
        details.classList.add(details.open ? openClass : closedClass);

        this.summary?.classList.toggle(closedClass, details.open);
      },
      { signal }
    );
  }

  /**
   * Closes sibling accordion rows when this one opens, if the parent has data-exclusive-open.
   *
   * @param {AbortSignal} signal
   */
  #applyExclusiveOpen(signal) {
    const accordion = this.closest('.accordion');

    if (!(accordion instanceof HTMLElement) || accordion.dataset.exclusiveOpen !== 'true') return;

    this.details.addEventListener(
      'toggle',
      () => {
        if (!this.details.open) return;

        accordion.querySelectorAll('accordion-custom').forEach((sibling) => {
          if (sibling !== this && sibling.details.open) {
            sibling.details.open = false;
          }
        });
      },
      { signal }
    );
  }

  /**
   * Handles keydown events for the accordion
   *
   * @param {KeyboardEvent} event - The keyboard event.
   */
  #handleKeyDown(event) {
    // Close the accordion when used as a menu
    if (event.key === 'Escape' && this.#closeWithEscape) {
      event.preventDefault();

      this.details.open = false;
      this.summary.focus();
    }
  }
}

if (!customElements.get('accordion-custom')) {
  customElements.define('accordion-custom', AccordionCustom);
}
